import { apiFetch } from '../utils/apiFetch';
import type { ApiEndpoints } from '../types/ApiEndpoints';

export interface PartnerSocialLink {
  platform: string;
  url: string;
}

export interface PartnerMediaItem {
  id?: string;
  url: string;
  type?: 'image' | 'video' | 'youtube' | 'instagram';
  text?: string | null;
  copyright?: string | null;
  sortOrder?: number;
}

export interface PartnerProfile {
  id: string | null;
  name: string | null;
  partnerCode: string | null;
  logo: string | null;
  websiteUrl: string | null;
  address: string | null;
  /** Admin-authored HTML "about" text (rendered via dangerouslySetInnerHTML). */
  htmlDescription: string | null;
  socialLinks: PartnerSocialLink[];
  media: PartnerMediaItem[];
  /** Count of the partner's public upcoming events (header stat). */
  eventsCount?: number;
  /**
   * The partner's data-source-provider short name — the frontend scopes the
   * map/search to this partner's events via /v3/search `providers=`. Null when
   * the partner has no data-source-provider. Kept for backward compatibility;
   * prefer the plural `dataSourceProviderShortNames`.
   */
  dataSourceProviderShortName?: string | null;
  /**
   * ALL of the partner's DSP short names. The backend events list spans every
   * DSP, so the map scope uses this (falling back to the singular field).
   */
  dataSourceProviderShortNames?: string[];
}

/**
 * Client for the public, read-only partner profile API. Mirrors the public
 * methods of ArtistProfileService (partners are edited only in beat/Nova).
 */
export class PartnerProfileService {
  constructor(
    private endpoints: Pick<ApiEndpoints, 'partnerProfiles'>,
    private apiKey?: string,
    private partnerKey?: string | null
  ) {}

  /** Public: fetch a partner profile by partner code. Returns null when not found. */
  async getPartnerByCode(code: string): Promise<PartnerProfile | null> {
    const res = await apiFetch(`${this.endpoints.partnerProfiles}/${encodeURIComponent(code)}`, {
      method: 'GET',
      apiKey: this.apiKey,
      partnerKey: this.partnerKey,
    });
    if (res.status === 404) {
      return null;
    }
    if (!res.ok) {
      throw new Error('Failed to load partner');
    }
    return res.json();
  }
}
